import type {
  HoldReason,
  InterestPostingFrequency,
  PassbookStatus,
  SavingsAccountStatus,
  SavingsProductClass,
  SavingsTransactionType,
} from '@/api/savings-types'
import { PREREQUISITE_GATES } from '@/api/savings-types'

/**
 * The words the savings screens use.
 *
 * Server spellings are the keys throughout, so a value that arrives from a later version of the
 * API and is not listed falls through to the raw token rather than to a friendly-looking guess.
 */

export const SAVINGS_ACCOUNT_STATUSES = ['Active', 'Dormant', 'Frozen', 'Closed'] as const

export const SAVINGS_PRODUCT_CLASSES = ['RegularSavings', 'SecurityDeposit'] as const

export const HOLD_REASONS = ['OffsetPending', 'Lien', 'CourtOrder'] as const

export const INTEREST_POSTING_FREQUENCIES = ['None', 'Monthly', 'Quarterly', 'Annually'] as const

const ACCOUNT_STATUS_LABELS: Record<SavingsAccountStatus, string> = {
  Active: 'Active',
  Dormant: 'Dormant',
  Frozen: 'Frozen',
  Closed: 'Closed',
}

export const savingsAccountStatusLabel = (status: SavingsAccountStatus) =>
  ACCOUNT_STATUS_LABELS[status] ?? status

const PRODUCT_CLASS_LABELS: Record<SavingsProductClass, string> = {
  RegularSavings: 'Regular savings',
  SecurityDeposit: 'Security deposit',
}

export const productClassLabel = (productClass: SavingsProductClass) =>
  PRODUCT_CLASS_LABELS[productClass] ?? productClass

/**
 * The two classes sit on separate general-ledger liabilities because the Bank of Ghana deposit
 * return reports them separately. This is the one-line version of that, for a form.
 */
const PRODUCT_CLASS_DESCRIPTIONS: Record<SavingsProductClass, string> = {
  RegularSavings: "The customer's own money. Withdrawable, and it ages into dormancy when idle.",
  SecurityDeposit:
    'Cash collateral under the group guarantee. Never withdrawable, and never dormant — it is not idle, it is doing its job.',
}

export const productClassDescription = (productClass: SavingsProductClass) =>
  PRODUCT_CLASS_DESCRIPTIONS[productClass] ?? ''

const HOLD_REASON_LABELS: Record<HoldReason, string> = {
  OffsetPending: 'Offset pending',
  Lien: 'Lien',
  CourtOrder: 'Court order',
}

export const holdReasonLabel = (reason: HoldReason) => HOLD_REASON_LABELS[reason] ?? reason

const HOLD_REASON_DESCRIPTIONS: Record<HoldReason, string> = {
  OffsetPending:
    'Placed by the loans module against a defaulting member’s security deposit while the guarantee offset is decided. Placing one by hand pre-empts that decision.',
  Lien: 'Money earmarked against an obligation this institution is owed.',
  CourtOrder: 'An external instruction. The note should name the order.',
}

export const holdReasonDescription = (reason: HoldReason) => HOLD_REASON_DESCRIPTIONS[reason] ?? ''

const FREQUENCY_LABELS: Record<InterestPostingFrequency, string> = {
  None: 'Never posts',
  Monthly: 'Monthly',
  Quarterly: 'Quarterly',
  Annually: 'Annually',
}

export const interestFrequencyLabel = (frequency: InterestPostingFrequency) =>
  FREQUENCY_LABELS[frequency] ?? frequency

const TRANSACTION_TYPE_LABELS: Record<SavingsTransactionType, string> = {
  Deposit: 'Deposit',
  Withdrawal: 'Withdrawal',
  WithdrawalFee: 'Withdrawal fee',
  TransferIn: 'Transfer in',
  TransferOut: 'Transfer out',
  SecurityDepositIn: 'Security deposit in',
  InterestPosting: 'Interest',
  GuaranteeOffset: 'Guarantee offset',
  Refund: 'Refund',
}

export const transactionTypeLabel = (type: SavingsTransactionType) =>
  TRANSACTION_TYPE_LABELS[type] ?? type

/**
 * Which way a line moves the balance. Used to sign the amount column on a statement, so a
 * withdrawal reads as a withdrawal without the operator having to know the type names.
 */
const CREDIT_TYPES = new Set<SavingsTransactionType>([
  'Deposit',
  'TransferIn',
  'SecurityDepositIn',
  'InterestPosting',
])

export const isCreditTransaction = (type: SavingsTransactionType) => CREDIT_TYPES.has(type)

const PASSBOOK_STATUS_DESCRIPTIONS: Record<PassbookStatus, string> = {
  Active: 'In the customer’s hands and in use.',
  Lost: 'Reported lost. Replace it rather than issuing a second book.',
  Replaced: 'Superseded by a later book.',
  Retired: 'Withdrawn from use.',
}

export const passbookStatusDescription = (status: PassbookStatus) =>
  PASSBOOK_STATUS_DESCRIPTIONS[status] ?? ''

/**
 * The loan prerequisite gates, in the words a teller says to the member at the counter. The
 * server sends the codes; nothing here invents one.
 */
const GATE_LABELS: Record<string, string> = {
  [PREREQUISITE_GATES.noSavingsAccount]: 'No savings account. One has to be opened first.',
  [PREREQUISITE_GATES.savingsAccountNotActive]:
    'The savings account is not active — it is dormant, frozen, or closed.',
  [PREREQUISITE_GATES.noSecurityDepositAccount]:
    'No security deposit account. The collateral has nowhere to sit.',
  [PREREQUISITE_GATES.securityDepositShort]:
    'The security deposit is below what the loan product requires. Take a top-up.',
  [PREREQUISITE_GATES.noPassbook]: 'No active passbook. Issue one.',
}

export const prerequisiteGateLabel = (gate: string) => GATE_LABELS[gate] ?? gate
